import type { LatLng } from "leaflet";
import _ from "lodash";
import { type RefObject, useCallback } from "react";

import type Light from "@/models/light";
import MapSettings from "@/models/map-settings";
import type { MarkerPosition } from "@/models/marker-position";
import type { MapHandle, MapMode } from "@/types";
import { snapToGrid } from "@/utils/map";

import type { MovePoint } from "./draggable-hook";

/**
 * Props for the `useFixtureMovement` hook.
 */
interface FixtureMovementProps {
    /**
     * A ref holding the current lights, used for overlap checks.
     */
    lightsRef: RefObject<Light[]>;
    /**
     * The ref holding the imperative handle of the `Map` component.
     */
    mapRef: RefObject<MapHandle | null>;
    /**
     * The map settings configuration.
     */
    mapSettings: MapSettings;
    /**
     * The global marker positions keyed by unique ID.
     */
    markers: Record<string, MarkerPosition>;
    /**
     * The active map mode.
     */
    mode: MapMode;
    /**
     * The mode-scoped marker positions keyed by unique ID.
     */
    modeMarkers: Record<string, MarkerPosition>;
    /**
     * Whether paste mode is active.
     */
    pasteModeActivated: boolean;
    /**
     * Callback to select a marker.
     */
    selectMarker: (marker: MarkerPosition) => void;
    /**
     * Whether light selection is enabled.
     */
    selectionEnabled: boolean;
    /**
     * Whether dragged fixtures snap to the ceiling tile grid.
     */
    snapping: boolean;
    /**
     * The toast notification callback.
     */
    toastr: (message: string, type?: string) => void;
    /**
     * Callback to update global lights.
     */
    updateLights: (lights: Light[]) => void;
    /**
     * Callback to update marker positions in the store.
     */
    updateMarkers: (markers: Record<string, MarkerPosition>) => void;
    /**
     * Callback to update mode lights.
     */
    updateModeLights: (lights: Light | Light[]) => void;
    /**
     * Callback to update mode-scoped marker positions.
     */
    updateModeMarkers: (markers: Record<string, MarkerPosition>) => void;
}

const overlapMessage =
    "We couldn't place all of the lights in your array. " +
    "The skipped lights were either out of bounds of the map dimensions, or overlapped existing lights.";

/**
 * Handles dragging lights and markers to new positions, and selecting a marker.
 *
 * @param params - The map handle ref, fixture state, and store callbacks.
 * @returns The move and select handlers plus the grid snapping helper.
 */
export const useFixtureMovement = ({
    lightsRef,
    mapRef,
    mapSettings,
    markers,
    mode,
    modeMarkers,
    pasteModeActivated,
    selectMarker,
    selectionEnabled,
    snapping,
    toastr,
    updateLights,
    updateMarkers,
    updateModeLights,
    updateModeMarkers,
}: FixtureMovementProps) => {
    const snapPoint = useCallback(
        (point: LatLng) =>
            snapping ? snapToGrid(point, mapSettings.ceilingDetails) : point,
        [snapping, mapSettings.ceilingDetails],
    );

    const onMoveLight = useCallback(
        (points: MovePoint[]) => {
            if (!mapRef.current) return;

            const { map } = mapRef.current;
            const currentLights = lightsRef.current;

            const noSelectedLights = _.filter(
                currentLights,
                (l) => !l.selected,
            );

            let movedLights = [];
            let anyOverlapped = false;

            _.forEach(points, ({ point, id }) => {
                const light = _.clone(
                    _.find(currentLights, (l) => l.uniqueId === id),
                );

                light.lastPoint = light.point;
                light.point = point;

                if (snapping) {
                    light.snappingToGrid(mapSettings);
                }

                const isOverlapping = light.isOverlapping(noSelectedLights);

                if (isOverlapping && !anyOverlapped) {
                    anyOverlapped = true;
                    return;
                }

                movedLights.push(light);
            });

            if (anyOverlapped) {
                toastr(overlapMessage, "warning");
                return;
            }

            if (mode.name === "view") {
                updateLights(movedLights);
                return;
            }

            if (mode.name === "add" && !map.movingLights) {
                movedLights = movedLights[0];
            }

            updateModeLights(movedLights);
        },
        [
            lightsRef,
            mapRef,
            mapSettings,
            mode.name,
            snapping,
            toastr,
            updateLights,
            updateModeLights,
        ],
    );

    const onMoveMarker = useCallback(
        (points: MovePoint[]) => {
            const modeUpdated: Record<string, MarkerPosition> = {};
            const globalUpdated: Record<string, MarkerPosition> = {};

            for (const { id, point } of points) {
                const modeMarker = modeMarkers[id];
                const globalMarker = markers[id];
                const marker = modeMarker ?? globalMarker;

                if (!marker) continue;

                const { lng: x, lat: y } = point;
                const updated = { ...marker, x, y };

                if (modeMarker) {
                    modeUpdated[id] = updated;
                } else {
                    globalUpdated[id] = updated;
                }
            }

            if (Object.keys(modeUpdated).length > 0) {
                updateModeMarkers(modeUpdated);
            }

            if (Object.keys(globalUpdated).length > 0) {
                updateMarkers(globalUpdated);
            }
        },
        [markers, modeMarkers, updateMarkers, updateModeMarkers],
    );

    const onSelectMarker = useCallback(
        (marker: MarkerPosition) => {
            if (pasteModeActivated || !selectionEnabled) return;

            const isViewMode = mode.name === "view";
            const isAddMode = mode.name === "add";

            const isManualSelectMode =
                mode.name === "select" && mode.props?.type === "Manual";

            if (isViewMode || isAddMode || isManualSelectMode) {
                selectMarker(marker);
            }
        },
        [
            mode.name,
            mode.props?.type,
            pasteModeActivated,
            selectMarker,
            selectionEnabled,
        ],
    );

    return { onMoveLight, onMoveMarker, onSelectMarker, snapPoint };
};
